import { BirthPlace } from './chart';
import { isValidZone } from './time';

interface CityFile {
    zones: string[];
    /**
     * [name, asciiName or 0, country code, lat, lon, zone index, other names joined by '|' or 0, name in
     * Brazilian Portuguese or 0], biggest cities first.
     */
    cities: [
        string,
        string | 0,
        string,
        number,
        number,
        number,
        string | 0,
        string | 0,
    ][];
}

export interface City extends BirthPlace {
    /** ISO 3166 country code. */
    country: string;
    /** What a Brazilian calls it, when that differs (Munique, Nova Iorque). */
    namePt?: string;
}

interface Indexed {
    city: City;
    /** Lower-case, accent-free names the search matches against. */
    keys: string[];
}

/** Lower-case and strip accents, so "sao paulo" finds "São Paulo". */
export const fold = (text: string): string =>
    text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/**
 * Zone names newer than the time-zone database of some devices. Each maps to the zone that has the
 * same rules for every date up to now (Aysen followed Santiago until 2025).
 */
const ZONE_FALLBACK: Record<string, string> = {
    'America/Coyhaique': 'America/Santiago',
};

const usableZone = (zone: string): string =>
    !isValidZone(zone) && ZONE_FALLBACK[zone] ? ZONE_FALLBACK[zone] : zone;

let loading: Promise<Indexed[]> | null = null;

/** Loads the city list once (about 1.5 MB, bundled: no network, works offline). */
export const loadCities = (): Promise<Indexed[]> => {
    if (!loading) {
        loading = import('./data/cities.json').then((module) => {
            const data = (module.default ?? module) as unknown as CityFile;
            const zones = data.zones.map(usableZone);
            return data.cities.map(
                ([name, ascii, country, lat, lon, zone, others, pt]) => ({
                    city: {
                        name,
                        country,
                        lat,
                        lon,
                        zone: zones[zone],
                        ...(pt ? { namePt: pt } : {}),
                    },
                    keys: [
                        fold(name),
                        ascii ? fold(ascii) : fold(name),
                        ...(others ? others.split('|') : []),
                        ...(pt ? [fold(pt)] : []),
                    ],
                })
            );
        });
        loading.catch(() => {
            loading = null;
        });
    }
    return loading;
};

/** Cities whose name starts with the query first (biggest first), then those that contain it. */
export const searchCities = (
    index: Indexed[],
    query: string,
    limit = 8
): City[] => {
    const q = fold(query);
    if (q.length < 2) return [];
    const starts: City[] = [];
    const contains: City[] = [];
    for (const { city, keys } of index) {
        if (keys.some((k) => k.startsWith(q))) starts.push(city);
        else if (keys.some((k) => k.includes(q))) contains.push(city);
        if (starts.length >= limit) break;
    }
    return [...starts, ...contains].slice(0, limit);
};

/** The time zones the device knows, for a place entered by hand. */
export const knownZones = (): string[] => {
    const intl = Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
    };
    try {
        return intl.supportedValuesOf?.('timeZone') ?? [];
    } catch {
        return [];
    }
};

export interface ManualPlace {
    name: string;
    lat: string;
    lon: string;
    zone: string;
}

/** Validates a place typed by hand; returns the place or the first thing that is wrong. */
export const parseManualPlace = (
    p: ManualPlace
): { place: BirthPlace } | { error: 'lat' | 'lon' | 'zone' } => {
    const lat = Number(p.lat.replace(',', '.'));
    const lon = Number(p.lon.replace(',', '.'));
    if (p.lat.trim() === '' || !Number.isFinite(lat) || Math.abs(lat) > 90)
        return { error: 'lat' };
    if (p.lon.trim() === '' || !Number.isFinite(lon) || Math.abs(lon) > 180)
        return { error: 'lon' };
    if (!isValidZone(p.zone)) return { error: 'zone' };
    const name = p.name.trim() || `${lat.toFixed(2)}°, ${lon.toFixed(2)}°`;
    return { place: { name, lat, lon, zone: p.zone } };
};

interface PhotonFeature {
    properties: {
        name?: string;
        countrycode?: string;
        state?: string;
    };
    geometry: { coordinates: [number, number] };
}

/**
 * Looks a place up on Photon (OpenStreetMap data, free, no key) for names the bundled list does not
 * have. Only the text typed in the search box is sent. The time zone is worked out on the device from
 * the coordinates. Returns [] on any failure, so the caller falls back to manual coordinates.
 */
export const searchOnline = async (
    query: string,
    signal?: AbortSignal
): Promise<City[]> => {
    const q = query.trim();
    if (q.length < 3) return [];
    try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&osm_tag=place`;
        const response = await fetch(url, { signal });
        if (!response.ok) return [];
        const { features } = (await response.json()) as {
            features: PhotonFeature[];
        };
        const tzlookup = (await import('tz-lookup')).default;
        const found: City[] = [];
        for (const { properties, geometry } of features ?? []) {
            const [lon, lat] = geometry.coordinates;
            if (!properties.name || !Number.isFinite(lat)) continue;
            let zone: string;
            try {
                zone = usableZone(tzlookup(lat, lon));
            } catch {
                continue;
            }
            if (!isValidZone(zone)) continue;
            const name = properties.state
                ? `${properties.name}, ${properties.state}`
                : properties.name;
            const duplicate = found.some(
                (c) =>
                    c.name === name &&
                    Math.abs(c.lat - lat) < 0.05 &&
                    Math.abs(c.lon - lon) < 0.05
            );
            if (!duplicate)
                found.push({
                    name,
                    country: (properties.countrycode ?? '').toUpperCase(),
                    lat: Math.round(lat * 1e4) / 1e4,
                    lon: Math.round(lon * 1e4) / 1e4,
                    zone,
                });
        }
        return found;
    } catch {
        return [];
    }
};
